<template>
  <div class="flex flex-col">
    <div
      class="bg-surface-container-low rounded-large relative mt-2 flex flex-col overflow-hidden border-[3px] border-transparent"
      :class="[
        error && 'border-error hover:border-on-error-container text-on-surface focus:border-error',
        !error &&
          'border-outline hover:border-on-surface text-on-surface-variant hover:text-on-surface focus-within:border-primary!',
      ]"
    >
      <BaseIcon
        v-if="leadingIcon"
        size="24"
        class="text-on-surface-variant absolute top-4 left-3"
        >{{ leadingIcon }}</BaseIcon
      >
      <input
        v-if="type === 'text' || type === 'email' || type === 'number'"
        :type="type"
        v-auto-focus="autofocus"
        v-model="inputValue"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxLength"
        :minlength="minLength"
        class="peer rounded-extra-small text-body-large relative z-0 h-14 w-full pt-4 outline-none"
        :class="[
          error && 'text-on-surface',
          !error && 'text-on-surface-variant hover:text-on-surface focus:text-on-surface',
          leadingIcon && 'pr-4 pl-13 focus:pl-[50px]',
          !leadingIcon && 'px-4',
        ]"
        placeholder=" "
      />
      <textarea
        v-if="type === 'multiText'"
        ref="textarea"
        v-model="inputValue"
        :readonly="readonly"
        class="peer rounded-extra-small text-body-large relative z-0 h-14 w-full pt-6 outline-none"
        :class="[
          error && 'border-error',
          !error && 'border-outline-variant',
          leadingIcon && 'pr-4 pl-13 focus:pl-[50px]',
          !leadingIcon && 'px-4',
        ]"
        :maxlength="maxLength"
        :minlength="minLength"
        placeholder=" "
      />
      <label
        class="text-on-surface-variant text-body-small peer-placeholder-shown:text-body-large peer-focus:text-body-small pointer-events-none absolute top-1.5 bg-inherit px-1 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-focus:top-1.5 peer-focus:peer-placeholder-shown:top-1.5 peer-placeholder-shown:peer-focus:translate-y-0"
        :class="[
          error &&
            'peer-placeholder-shown:peer-focus:text-error peer-placeholder-shown:text-error peer-focus:text-error text-error peer-hover:text-on-error-container',
          !error &&
            'peer-placeholder-shown:peer-focus:text-primary peer-placeholder-shown:text-on-surface-variant peer-focus:text-primary peer-hover:text-on-surface',
          leadingIcon && 'left-3 peer-placeholder-shown:left-13 peer-focus:left-3',
          !leadingIcon && 'left-3 peer-placeholder-shown:left-3',
        ]"
      >
        {{ label }}
        {{ !hideDecorators ? (required ? '*' : `(${$t('common.optional')})`) : '' }}
      </label>
    </div>
    <p v-if="error" class="text-error text-body-small mt-1 px-4">
      {{ $t(error, { field: label }) }}
    </p>
  </div>
</template>

<script lang="ts" setup>
  import { useTextareaAutosize } from '@vueuse/core'
  import type { InputType } from './input-type'
  import { computed, useTemplateRef, type MaybeRef } from 'vue'
  import BaseIcon from '../icon/BaseIcon.vue'

  interface Props {
    type: InputType
    error?: string
    leadingIcon?: string
    label?: string
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    manualFocused?: boolean
    hideDecorators?: boolean
    maxLength?: number
    minLength?: number
    autofocus?: boolean
  }

  defineProps<Props>()
  const model = defineModel<string | undefined>()

  const inputValue = computed({
    get: () => model.value ?? '',
    set: (value: string) => {
      model.value = value === '' ? undefined : value
    },
  })

  const textarea = useTemplateRef('textarea')

  useTextareaAutosize({ input: inputValue as MaybeRef, element: textarea, styleProp: 'minHeight' })
</script>
